<template>
  <tr
    class="bg-white"
    :class="{
      'pointer hover:!bg-brand-50': !!onRowSelected,
      '!bg-brand-50': selectedRowKey === row.id,
    }"
    @click="onRowSelected ? onRowSelected(row) : null"
  >
    <template v-for="column in columns">
      <td
        v-if="column.type !== 'hidden'"
        :key="'dataTableRowItem_' + row.id + '_' + column.key"
        class="p-4 border-b border-gray-200 text-brand-900 text-sm font-medium"
        :class="{
          'text-center': column.align === 'center',
          'text-right': column.align === 'right',
          [column.classes]: column.classes,
        }"
      >
        <span
          v-if="
            !getObjectValue(column.key, row) &&
            getObjectValue(column.key, row) !== false
          "
          >{{ t("components.inputs.expansion.no_value") }}</span
        >
        <span v-else-if="column.type === 'date'">{{
          formatDate(getObjectValue(column.key, row))
        }}</span>
        <span v-else-if="column.type === 'dateTime'">{{
          formatDateTime(getObjectValue(column.key, row))
        }}</span>
        <span v-else-if="column.type === 'space'" class="whitespace-nowrap">
          <div class="text-gray-900 font-medium">
            {{ getTenantLabel(getObjectValue(column.key, row)) }}
          </div>
          <div class="text-gray-500 font-normal">
            {{ getSpacesLabel(getObjectValue(column.key, row)) }}
          </div>
        </span>
        <span v-else-if="column.type === 'visibility' && column.onSelected">
          <VisibilitySelector
            @change="column.onSelected(row.id, $event)"
            :active="getObjectValue(column.key, row) ? 'public' : 'private'"
            :disabled="!canUpdate"
          />
        </span>
        <span
          v-else-if="column.type === 'count'"
          class="text-gray-700 grid place-items-center w-5 h-5 rounded-full bg-gray-100"
          :class="{
            'mx-auto': column.align === 'center',
            'mr-0': column.align === 'right',
          }"
          >{{ getValueLength(getObjectValue(column.key, row)) }}</span
        >
        <span v-else>
          {{ parseValue(getObjectValue(column.key, row)) }}
        </span>
      </td>
    </template>

    <td class="p-4 border-b border-gray-200 text-brand-900 text-xs font-medium">
      <div class="text-right">
        <InlineActionsMenu
          v-if="showInlineActionsMenu"
          :options="[
            {
              key: 'delete',
              label: deleteLabel ?? t('admin.settings.delete'),
              icon: TrashOne,
              onClick: () => emit('delete', row?.id),
              show: () => row?.id && canDelete,
            },
            ...actions,
          ]"
          :eventData="row"
          visible
        />
      </div>
    </td>
  </tr>
</template>

<script setup>
import { usePage } from "@inertiajs/vue3";
import { format } from "date-fns";
import { useI18n } from "vue-i18n";
import VisibilitySelector from "@/Tailwind/Components/Documents/VisibilitySelector.vue";
import InlineActionsMenu from "@/Tailwind/Components/InlineActionsMenu.vue";
import TrashOne from "@/Tailwind/Icon/TrashOne.vue";
import { computed } from "vue";

const props = defineProps({
  row: {
    type: Object,
    required: true,
  },
  columns: {
    type: Array,
    required: true,
  },
  actions: {
    type: Array,
  },
  selectedRowKey: {
    type: String,
  },
  onRowSelected: {
    type: Function,
  },
  canDelete: {
    type: Boolean,
  },
  canUpdate: {
    type: Boolean,
  },
  deleteLabel: {
    type: String,
  },
});

const { t } = useI18n();

const emit = defineEmits(["delete"]);

const showInlineActionsMenu = computed(() => {
  if (props.canDelete) {
    return true;
  }

  // Hide menu if there are no other actions.
  if (!props.actions) {
    return false;
  }

  // If there is at least one action, don't hide the menu.
  return props.actions.some((action) => {
    // If no condition exists, the action will always be shown.
    if (!action.show) {
      return true;
    }

    return action.show();
  });
});

const formatDateTime = (date) => {
  if (Array.isArray(date)) {
    return date.map((d) => format(new Date(d), "dd/MM/yyyy HH:mm")).join(", ");
  }

  return format(new Date(date), "dd/MM/yyyy HH:mm");
};

const formatDate = (date) => {
  if (Array.isArray(date)) {
    return date.map((d) => format(new Date(d), "dd/MM/yyyy")).join(", ");
  }

  return format(new Date(date), "dd/MM/yyyy");
};

const parseValue = (value) => {
  if (Array.isArray(value)) {
    return value.join(", ");
  }

  return value;
};

const getValueLength = (value) => {
  if (Array.isArray(value)) {
    return value.length;
  }

  return value;
};

const getObjectValue = (path, obj) => {
  return path.split(".").reduce(function (prev, curr) {
    return prev ? prev[curr] : null;
  }, obj || self);
};

const tenants = usePage().props.tenants;
function getTenantLabel(spaces) {
  if (!spaces || spaces.length === 0) {
    return t("components.spaces.none");
  }

  const space = spaces[0];
  const tenant = tenants.find((tenant) => tenant.id === space.tenant_id);
  return tenant.name;
}

function getSpacesLabel(spaces) {
  if (!spaces || spaces.length === 0) {
    return t("components.spaces.none");
  }

  if (spaces.length > 1) {
    return t(
      "admin.settings.documents.spaces.title",
      spaces.length
    ).toLowerCase();
  }

  const space = spaces[0];
  return space.name;
}
</script>
